import { useEffect, useMemo } from 'react';
import useCrmStore from '../stores/crmStore';
import useAuthStore from '../stores/authStore';
import GerenteDashboard from './GerenteDashboard';
import { Wallet, ShieldCheck, AlertTriangle, Phone, Activity, Calendar, BarChart3 } from 'lucide-react';

function KPICard({ label, value, delta, icon: Icon, color }) {
  return (
    <div className={`kpi-card kpi-${color}`} data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="kpi-header">
        <span className="kpi-label">{label}</span>
        <div className={`kpi-icon kpi-icon-${color}`}><Icon size={18} /></div>
      </div>
      <div className="kpi-value">{value}</div>
      {delta && <div className={`kpi-delta ${delta.startsWith('+') ? 'up' : delta.startsWith('-') ? 'down' : 'up'}`}>{delta}</div>}
    </div>
  );
}

function BarChart({ items, maxVal }) {
  const max = maxVal || Math.max(...items.map(i => i.value), 1);
  return (
    <div className="bar-chart">
      {items.map((item, idx) => (
        <div key={item.id ?? `${item.label}-${idx}`} className="bar-row">
          <span className="bar-label">{item.label}</span>
          <div className="bar-track">
            <div className="bar-fill" style={{ width: `${(item.value / max) * 100}%`, background: item.color || 'var(--kompa-accent)' }} />
          </div>
          <span className="bar-val">{item.value}</span>
        </div>
      ))}
    </div>
  );
}

const fmt = (n) => `$${Number(n).toLocaleString('es-VE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export default function DashboardPage() {
  const metrics = useCrmStore(s => s.metrics);
  const fetchMetrics = useCrmStore(s => s.fetchMetrics);
  const user = useAuthStore(s => s.user);

  useEffect(() => { fetchMetrics(); }, [fetchMetrics]);

  const planKompa = metrics?.plan_distribution?.kompa_plus || 0;
  const planAcelerado = metrics?.plan_distribution?.acelerado || 0;

  const planDistItems = useMemo(() => ([
    { label: 'KOMPA+', value: planKompa, color: '#2563eb' },
    { label: 'Acelerado', value: planAcelerado, color: '#d97706' },
  ]), [planKompa, planAcelerado]);

  if (!metrics) return <div className="page-loading">Cargando dashboard...</div>;

  // Gerente sees a dedicated, consolidated view mandated by sales management.
  if (user?.role === 'gerente') {
    return <GerenteDashboard metrics={metrics} />;
  }

  return (
    <div data-testid="dashboard-page" className="dashboard-page">
      <div className="dash-grid">
        <KPICard label="Ahorro Total Consolidado" value={fmt(metrics.ahorro_total)} delta={`${metrics.clientes_activos} clientes activos`} icon={Wallet} color="green" />
        <KPICard label="Tasa Verificacion KYC" value={`${metrics.tasa_kyc}%`} delta={`${metrics.kyc_completados} verificados`} icon={ShieldCheck} color="blue" />
        <KPICard label="Clientes en Riesgo" value={metrics.en_riesgo} delta={metrics.en_riesgo > 0 ? `>60 dias sin pago` : 'Sin alertas'} icon={AlertTriangle} color={metrics.en_riesgo > 0 ? 'red' : 'green'} />
        <KPICard label="Llamadas hoy" value={metrics.llamadas_hoy} delta="Actividad del dia" icon={Phone} color="purple" />
      </div>

      <div className="dash-grid-2">
        <div className="dash-card">
          <h3>Pipeline SNBL por Etapa</h3>
          <BarChart
            items={metrics.pipeline_stages?.map(s => ({ label: s.label, value: s.count, color: s.color })) || []}
          />
        </div>
        <div className="dash-card">
          <h3>Rendimiento de Agentes</h3>
          {metrics.agent_stats?.length > 0 ? (
            <BarChart items={metrics.agent_stats.map(a => ({ id: a.id, label: a.name, value: a.leads, color: '#d97706' }))} />
          ) : (
            <p className="text-muted">Sin datos de agentes</p>
          )}
        </div>
      </div>

      <div className="dash-grid-3">
        <div className="dash-card">
          <h3><Activity size={16} /> Actividad Reciente</h3>
          <div className="activity-feed">
            {metrics.recent_activities?.map(act => (
              <div key={act.id} className="activity-item">
                <div className={`activity-dot act-${act.tipo}`} />
                <div className="activity-content">
                  <span className="activity-desc">{act.descripcion}</span>
                  <span className="activity-meta">{act.agente_nombre} &middot; {act.tipo}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="dash-card">
          <h3><BarChart3 size={16} /> Distribucion de Planes</h3>
          <BarChart items={planDistItems} />
          <div className="quick-stats" style={{ marginTop: 12 }}>
            <div className="qs-row"><span>Liquidaciones completadas</span><strong>{metrics.liquidaciones || 0}</strong></div>
          </div>
        </div>

        <div className="dash-card">
          <h3><Calendar size={16} /> Resumen Rapido</h3>
          <div className="quick-stats">
            <div className="qs-row"><span>Total Leads</span><strong>{metrics.total_leads}</strong></div>
            <div className="qs-row"><span>Clientes activos</span><strong>{metrics.clientes_activos}</strong></div>
            <div className="qs-row"><span>Leads nuevos hoy</span><strong>{metrics.pipeline_stages?.find(s => s.id === 'nuevo_lead')?.count || 0}</strong></div>
            <div className="qs-row"><span>En riesgo</span><strong style={{ color: metrics.en_riesgo > 0 ? 'var(--red)' : 'inherit' }}>{metrics.en_riesgo}</strong></div>
          </div>
        </div>
      </div>
    </div>
  );
}
